// SCRUM-449: the playbook's slot kinds, as the Campaigns page shows and
// edits them. The API (campaign-schedule.ts) resolves each in the event's own
// timezone and is the source of truth for the bounds below.
//
// - repeat:   { kind: 'repeat', everyDays, at } — every N days at a time.
// - relative: { kind: 'relative', anchor, direction, unit, amount, at? } —
//   N days (at a time) or N hours before/after one of the event's dates.

export const SLOT_KINDS = ['repeat', 'relative'];

export const SLOT_KIND_LABEL = {
  repeat: 'Every N days',
  relative: 'Relative to a date',
};

export const ANCHORS = ['registrationOpens', 'eventStarts', 'eventEnds', 'salesEnd'];

export const ANCHOR_LABEL = {
  registrationOpens: 'Registration opens',
  eventStarts: 'Event starts',
  eventEnds: 'Event ends',
  salesEnd: 'Sales end',
};

/** The API's bounds (campaign-schedule.ts). */
export const RANGE = {
  everyDays: { min: 1, max: 90 },
  days: { min: 0, max: 90 },
  hours: { min: 1, max: 720 },
};

const AT_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/** "19:00" → "7:00 PM". */
export function formatTime12(hhmm) {
  const [h, m] = String(hhmm ?? '').split(':').map((n) => parseInt(n, 10));
  if (Number.isNaN(h)) return String(hhmm ?? '');
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(Number.isNaN(m) ? 0 : m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

export function slotLabel(slot) {
  const at = formatTime12(slot.at);
  switch (slot.kind) {
    case 'repeat': {
      const n = Number(slot.everyDays);
      return n === 1 ? `Every day · ${at}` : `Every ${slot.everyDays} days · ${at}`;
    }
    case 'relative': {
      const anchor = ANCHOR_LABEL[slot.anchor] ?? String(slot.anchor);
      const n = Number(slot.amount);
      if (slot.unit === 'hours') return `${slot.amount} h ${slot.direction} ${anchor}`;
      if (n === 0) return `${anchor} day · ${at}`;
      return `${slot.amount} ${n === 1 ? 'day' : 'days'} ${slot.direction} ${anchor} · ${at}`;
    }
    default:
      return String(slot.kind);
  }
}

const DEFAULTS = {
  repeat: { everyDays: 1, at: '19:00' },
  relative: { anchor: 'salesEnd', direction: 'before', unit: 'days', amount: 1, at: '19:00' },
};

/** A new slot of `kind` with an `s<n>` id unused by `existing`. */
export function newSlot(kind, existing = []) {
  const max = existing.reduce((acc, s) => {
    const m = /^s(\d+)$/.exec(String(s.id ?? ''));
    return m ? Math.max(acc, Number(m[1])) : acc;
  }, 0);
  return { id: `s${max + 1}`, kind, ...DEFAULTS[kind] };
}

/** A relative slot switched to `unit`: hours drop `at` (default 4 h), days
 * get `at` back (default 1 day at 7 PM). */
export function withUnit(slot, unit) {
  const { at, ...rest } = slot;
  return unit === 'hours'
    ? { ...rest, unit: 'hours', amount: 4 }
    : { ...rest, unit: 'days', amount: 1, at: '19:00' };
}

/** A typed number → `{ value }` when it is a whole number within
 * `RANGE[field]`, else `{ error }`. `field` is 'everyDays', 'days' or 'hours'. */
export function parseCount(raw, field) {
  const { min, max } = RANGE[field];
  const text = String(raw ?? '').trim();
  const n = Number(text);
  if (text === '' || !Number.isInteger(n) || n < min || n > max) {
    const what = field === 'hours' ? 'hours' : 'days';
    return { error: `Enter a whole number of ${what} from ${min} to ${max}.` };
  }
  return { value: n };
}

/** The first problem with a stored slot, mirroring the API's validateSlots,
 * or null when the API would accept it. */
export function slotError(slot) {
  if (slot.kind === 'repeat') {
    return parseCount(slot.everyDays, 'everyDays').error ?? (AT_RE.test(slot.at ?? '') ? null : 'Enter a time.');
  }
  if (slot.kind !== 'relative') return `Unknown slot kind "${slot.kind}".`;
  if (!ANCHORS.includes(slot.anchor)) return 'Pick a date.';
  if (slot.direction !== 'before' && slot.direction !== 'after') return 'Pick before or after.';
  if (slot.unit === 'hours') {
    if (slot.at !== undefined) return 'Hour slots have no time.';
    return parseCount(slot.amount, 'hours').error ?? null;
  }
  if (slot.unit !== 'days') return 'Pick days or hours.';
  return parseCount(slot.amount, 'days').error ?? (AT_RE.test(slot.at ?? '') ? null : 'Enter a time.');
}
